import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Download, Share2, BookOpen, Printer, Check, ArrowLeft, Mail, Headphones, Package } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import ShareButtons from './ShareButtons';

const AutobiographyViewer = ({ autobiographyData, onOrderHardcover, onBack }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isCopied, setIsCopied] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [isPublic, setIsPublic] = useState(autobiographyData?.is_public || false);

  const handleDownloadPDF = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/autobiography/${autobiographyData.id}/download/pdf`);
      
      if (!response.ok) {
        throw new Error('Failed to download PDF');
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${autobiographyData.title.replace(/ /g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast({
        title: "Download Started",
        description: "Your autobiography is being downloaded as PDF.",
      });
    } catch (error) {
      console.error('PDF download error:', error);
      toast({
        title: "Download Failed",
        description: "Unable to download PDF. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleDownloadText = () => {
    const blob = new Blob([autobiographyData.content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${autobiographyData.title.replace(/ /g, '_')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast({
      title: "Download Complete",
      description: "Your autobiography has been downloaded as a text file.",
    });
  };

  const handleCopyText = () => {
    navigator.clipboard.writeText(autobiographyData.content);
    setIsCopied(true);
    toast({
      title: "Copied to Clipboard",
      description: "Your autobiography text has been copied.",
    });
    setTimeout(() => setIsCopied(false), 2000);
  };

  const handleSendEmail = async () => {
    if (!emailInput.trim()) {
      toast({ title: "Email Required", description: "Please enter an email address.", variant: "destructive" });
      return;
    }
    setEmailSending(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${BACKEND_URL}/api/autobiography/${autobiographyData.id}/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailInput }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast({ title: "Email Sent!", description: `Your autobiography has been sent to ${emailInput}` });
        setShowEmailForm(false);
        setEmailInput('');
      } else {
        throw new Error(data.detail || 'Failed to send email');
      }
    } catch (err) {
      toast({ title: "Email Failed", description: err.message, variant: "destructive" });
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Back Button */}
        {onBack && (
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6 transition-colors"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Dashboard</span>
          </button>
        )}

        {/* Success Banner */}
        <div className="glass-effect rounded-2xl p-6 mb-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-400 to-emerald-400 rounded-full mb-4">
            <Check className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Your Autobiography is Ready!
          </h1>
          <p className="text-gray-600">
            {autobiographyData.word_count.toLocaleString()} words • {autobiographyData.chapters} chapters
          </p>
        </div>

        {/* Action Buttons */}
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <Button
            onClick={handleDownloadPDF}
            className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white py-6 text-lg flex items-center justify-center gap-2 rounded-xl border-0"
          >
            <Download className="w-5 h-5" />
            Download PDF
          </Button>
          
          <Button
            onClick={handleDownloadText}
            variant="outline"
            className="py-6 text-lg flex items-center justify-center gap-2 rounded-xl"
          >
            <Download className="w-5 h-5" />
            Download Text
          </Button>

          <Button
            onClick={handleCopyText}
            variant="outline"
            className="py-6 text-lg flex items-center justify-center gap-2 rounded-xl"
          >
            {isCopied ? <Check className="w-5 h-5" /> : <Share2 className="w-5 h-5" />}
            {isCopied ? 'Copied!' : 'Copy Text'}
          </Button>

          <Button
            onClick={() => {
              if (onOrderHardcover) onOrderHardcover();
              else if (autobiographyData?.id) navigate(`/hardcover-order/${autobiographyData.id}`);
            }}
            className="bg-gradient-to-r from-yellow-400 to-orange-400 hover:from-yellow-500 hover:to-orange-500 text-white py-6 text-lg flex items-center justify-center gap-2 rounded-xl border-0"
            data-testid="order-hardcover-btn"
          >
            <Printer className="w-5 h-5" />
            Order Hardcover Book
          </Button>

          <Button
            onClick={() => navigate(`/bundle-order/${autobiographyData.id}`)}
            className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white py-6 text-lg flex items-center justify-center gap-2 rounded-xl border-0"
            data-testid="order-bundle-btn"
          >
            <Package className="w-5 h-5" />
            Complete Bundle (Book + Audio + PDF)
          </Button>

          <Button
            onClick={() => setShowEmailForm(!showEmailForm)}
            variant="outline"
            className="py-6 text-lg flex items-center justify-center gap-2 rounded-xl"
            data-testid="email-btn"
          >
            <Mail className="w-5 h-5" />
            Email to Me
          </Button>

          <Button
            onClick={() => setShowShare(!showShare)}
            variant="outline"
            className="py-6 text-lg flex items-center justify-center gap-2 rounded-xl"
            data-testid="social-share-btn"
          >
            <Share2 className="w-5 h-5" />
            Share Story
          </Button>
        </div>

        {/* Share Panel */}
        {showShare && (
          <div className="mb-8 bg-white rounded-2xl shadow-md p-6 fade-in" data-testid="share-panel">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Share2 className="w-4 h-4 text-purple-500" /> Share Your Story
            </h3>
            <ShareButtons
              autobiographyId={autobiographyData?.id}
              isPublic={isPublic}
              onTogglePublic={async () => {
                const newState = !isPublic;
                try {
                  await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/autobiography/${autobiographyData.id}/toggle-public`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ is_public: newState }),
                  });
                  setIsPublic(newState);
                  toast({ title: newState ? 'Story is now public!' : 'Story is now private' });
                } catch {
                  toast({ title: 'Failed to update visibility', variant: 'destructive' });
                }
              }}
            />
          </div>
        )}

        {/* Email Form */}
        {showEmailForm && (
          <div className="mb-8 bg-white rounded-2xl shadow-md p-6 fade-in" data-testid="email-form">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><Mail className="w-4 h-4 text-purple-500" /> Send autobiography to your email</h3>
            <div className="flex gap-3">
              <input
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="your@email.com"
                className="flex-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-purple-400 focus:ring-2 focus:ring-purple-100 outline-none text-sm"
                data-testid="email-input"
              />
              <Button
                onClick={handleSendEmail}
                disabled={emailSending}
                className="bg-gradient-to-r from-pink-500 to-purple-500 text-white px-6 rounded-xl border-0"
                data-testid="send-email-btn"
              >
                {emailSending ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : 'Send'}
              </Button>
            </div>
            <p className="text-xs text-gray-400 mt-2">The autobiography PDF will be attached to the email.</p>
          </div>
        )}

        {/* Book Preview */}
        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
          <div className="max-w-3xl mx-auto">
            {/* Title Page */}
            <div className="text-center mb-12 pb-8 border-b-2 border-gray-200">
              <BookOpen className="w-12 h-12 text-purple-400 mx-auto mb-6" />
              <h2 className="text-4xl font-serif font-bold text-gray-800 mb-3">
                {autobiographyData.title}
              </h2>
              <p className="text-xl text-gray-600 italic">
                {autobiographyData.subtitle}
              </p>
            </div>

            {/* Content */}
            <div className="prose prose-lg max-w-none">
              <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                {autobiographyData.content.split('\n').map((line, index) => {
                  if (line.startsWith('###')) {
                    return (
                      <h3 key={index} className="text-2xl font-bold text-gray-800 mt-8 mb-4">
                        {line.replace('###', '').trim()}
                      </h3>
                    );
                  } else if (line.startsWith('##')) {
                    return (
                      <h2 key={index} className="text-3xl font-bold text-gray-800 mt-6 mb-3 text-center italic">
                        {line.replace('##', '').trim()}
                      </h2>
                    );
                  } else if (line.startsWith('#')) {
                    return (
                      <h1 key={index} className="text-4xl font-bold text-gray-800 mb-2 text-center">
                        {line.replace('#', '').trim()}
                      </h1>
                    );
                  } else if (line.trim() === '---') {
                    return <hr key={index} className="my-8 border-gray-300" />;
                  } else if (line.trim() !== '') {
                    return (
                      <p key={index} className="mb-4 text-justify">
                        {line}
                      </p>
                    );
                  }
                  return <br key={index} />;
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Share Section */}
        <div className="mt-8 text-center glass-effect rounded-2xl p-6">
          <p className="text-gray-700 mb-4">
            Love your autobiography? Share the joy with family and friends!
          </p>
          <Button
            variant="outline"
            className="rounded-full"
          >
            Share Your Experience
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AutobiographyViewer;
